import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Chip, Typography } from '@mui/material';
import {
  EAssessmentType,
  EAssessmentStatus,
} from '../../../../../apis/academics/academicsAssessments.interfaces';
import { TTranslateTextType } from 'src/hooks/useTranslationHook';

interface AssessmentRow {
  id: string;
  name: string;
  assessmentType: EAssessmentType;
  status: EAssessmentStatus;
  maxScore: number;
  averageScore?: number;
  averagePercentage?: number;
  totalStudents?: number;
  scheduledDate?: string;
  conductedDate?: string;
  dueDate?: string;
  gradedDate?: string;
  weightage?: number;
}

const getStatusColor = (status: EAssessmentStatus) => {
  switch (status) {
    case EAssessmentStatus.DRAFT:
      return 'default';
    case EAssessmentStatus.SCHEDULED:
      return 'info';
    case EAssessmentStatus.IN_PROGRESS:
      return 'warning';
    case EAssessmentStatus.COMPLETED:
      return 'success';
    case EAssessmentStatus.GRADED:
      return 'success';
    case EAssessmentStatus.CANCELLED:
      return 'error';
    default:
      return 'default';
  }
};

const formatDate = (dateString?: string) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString();
  } catch {
    return dateString;
  }
};

export const getAssessmentsGridColumns = (
  translateText: TTranslateTextType
): GridColDef<AssessmentRow>[] => {
  return [
    {
      field: 'name',
      headerName: translateText('academics.assessments.columns.assessmentName'),
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'assessmentType',
      headerName: translateText('academics.assessments.columns.type'),
      width: 130,
      renderCell: (params: GridRenderCellParams<AssessmentRow>) => {
        const type = params.value as EAssessmentType;
        return (
          <Chip label={type} size="small" variant="outlined" color="primary" />
        );
      },
    },
    {
      field: 'status',
      headerName: translateText('academics.assessments.columns.status'),
      width: 130,
      renderCell: (params: GridRenderCellParams<AssessmentRow>) => {
        const status = params.value as EAssessmentStatus;
        return (
          <Chip label={status} size="small" color={getStatusColor(status)} />
        );
      },
    },
    {
      field: 'maxScore',
      headerName: translateText('academics.assessments.columns.maxScore'),
      width: 110,
      type: 'number',
    },
    {
      field: 'averageScore',
      headerName: translateText('academics.assessments.columns.avgScore'),
      width: 110,
      type: 'number',
      renderCell: (params: GridRenderCellParams<AssessmentRow>) => {
        const score = params.value as number | undefined;
        const percentage = params.row.averagePercentage;
        if (score === undefined || score === null) return '-';
        return (
          <>
            {percentage !== undefined ? (
              <Typography variant="body2" >
                {percentage.toFixed(2)}%
              </Typography>
            ) : '-'}
          </>
        );
      },
    },
    {
      field: 'totalStudents',
      headerName: translateText('academics.assessments.columns.students'),
      width: 100,
      type: 'number',
      renderCell: (params: GridRenderCellParams<AssessmentRow>) => {
        const students = params.value as number | undefined;
        return students !== undefined ? students : '-';
      },
    },
    {
      field: 'scheduledDate',
      headerName: translateText('academics.assessments.columns.scheduledDate'),
      width: 140,
      renderCell: (params: GridRenderCellParams<AssessmentRow>) => {
        return formatDate(params.value as string | undefined);
      },
    },
    {
      field: 'dueDate',
      headerName: translateText('academics.assessments.columns.dueDate'),
      width: 140,
      renderCell: (params: GridRenderCellParams<AssessmentRow>) => {
        return formatDate(params.value as string | undefined);
      },
    },
    {
      field: 'conductedDate',
      headerName: translateText('academics.assessments.columns.conductedDate'),
      width: 140,
      renderCell: (params: GridRenderCellParams<AssessmentRow>) => {
        return formatDate(params.value as string | undefined);
      },
    },
    {
      field: 'weightage',
      headerName: translateText('academics.assessments.columns.weightage'),
      width: 110,
      type: 'number',
      renderCell: (params: GridRenderCellParams<AssessmentRow>) => {
        const weightage = params.value as number | undefined;
        return weightage !== undefined ? `${weightage}%` : '-';
      },
    },
  ];
};

interface AssessmentDetailsRow {
  id: string;
  studentName: string;
  obtainedScore: number;
  maxScore: number;
  percentage: number;
  date: string;
}

interface SubjectRow {
  id: number;
  courseId: number;
  courseName: string;
  subjectCode?: string;
  subjectType?: string;
  scheduledDuration?: number;
  scheduledDate?: string;
  totalStudents?: number;
  averageScore?: number;
  averagePercentage?: number;
}

export const getAssessmentSubjectsGridColumns = (
  translateText: TTranslateTextType
): GridColDef<SubjectRow>[] => {
  return [
    {
      field: 'courseName',
      headerName: translateText('academics.assessments.columns.subjectName') || 'Subject Name',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'subjectCode',
      headerName: translateText('academics.assessments.columns.subjectCode') || 'Subject Code',
      width: 130,
      renderCell: (params: GridRenderCellParams<SubjectRow>) => {
        return params.value || '-';
      },
    },
    {
      field: 'scheduledDuration',
      headerName: translateText('academics.assessments.columns.duration') || 'Duration',
      width: 120,
      type: 'number',
      renderCell: (params: GridRenderCellParams<SubjectRow>) => {
        const duration = params.value as number | undefined;
        if (duration === undefined || duration === null) return '-';
        const hours = Math.floor(duration / 60);
        const minutes = duration % 60;
        if (hours > 0) {
          return `${hours}h ${minutes}m`;
        }
        return `${minutes}m`;
      },
    },
    {
      field: 'scheduledDate',
      headerName: translateText('academics.assessments.columns.scheduledDate'),
      width: 140,
      renderCell: (params: GridRenderCellParams<SubjectRow>) => {
        return formatDate(params.value as string | undefined);
      },
    },
    {
      field: 'totalStudents',
      headerName: translateText('academics.assessments.columns.students'),
      width: 100,
      type: 'number',
      renderCell: (params: GridRenderCellParams<SubjectRow>) => {
        const students = params.value as number | undefined;
        return students !== undefined ? students : '-';
      },
    },
    {
      field: 'averageScore',
      headerName: translateText('academics.assessments.columns.avgScore'),
      width: 110,
      type: 'number',
      renderCell: (params: GridRenderCellParams<SubjectRow>) => {
        const score = params.value as number | undefined;
        const percentage = params.row.averagePercentage;
        if (score === undefined || score === null) return '-';
        return (
          <>
            {percentage !== undefined ? (
              <Typography variant="body2" >
                {percentage.toFixed(2)}%
              </Typography>
            ) : '-'}
          </>
        );
      },
    },
  ];
};

export const getAcademicsAssessmentDetailsGridColumns = (
  translateText: TTranslateTextType
): GridColDef<AssessmentDetailsRow>[] => {
  return [
    {
      field: 'studentName',
      headerName: translateText('academics.assessments.columns.studentName'),
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'obtainedScore',
      headerName: translateText('academics.assessments.columns.obtainedScore'),
      width: 130,
      type: 'number',
    },
    {
      field: 'maxScore',
      headerName: translateText('academics.assessments.columns.maxScore'),
      width: 110,
      type: 'number',
    },
    {
      field: 'percentage',
      headerName: translateText('academics.assessments.columns.percentage'),
      width: 120,
      type: 'number',
      renderCell: (params: GridRenderCellParams<AssessmentDetailsRow>) => {
        const percentage = params.value as number;
        return `${percentage.toFixed(2)}%`;
      },
    },
    {
      field: 'date',
      headerName: translateText('academics.assessments.columns.date'),
      width: 140,
      renderCell: (params: GridRenderCellParams<AssessmentDetailsRow>) => {
        return formatDate(params.value as string | undefined);
      },
    },
  ];
};
